import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  tenantApiKeyApi,
  type TenantApiKeyRecord,
} from '@/lib/api';
import {
  Key, Plus, Trash2, Copy, Check, X, Shield,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PageGuide } from '@/components/PageGuide';
import { SettingsEmptyState } from '@/components/SettingsEmptyState';
import styles from './_auth.settings.api-keys.module.css';

export const Route = createFileRoute('/_auth/settings/api-keys')({
  component: ApiKeysPage,
});

// ── Scope categories for grouped display ──────────────────
const SCOPE_CATEGORIES: Record<string, string[]> = {
  'Agents': ['agents:read', 'agents:write'],
  'Conversations': ['conversations:read', 'conversations:write'],
  'Chat': ['chat:write'],
  'Knowledge': ['knowledge:read', 'knowledge:write'],
  'Skills': ['skills:read', 'skills:write'],
  'Tools': ['tools:read', 'tools:write'],
  'Workflows': ['workflows:read', 'workflows:write'],
  'Triggers': ['triggers:read', 'triggers:write'],
  'Guardrails': ['guardrails:read', 'guardrails:write'],
  'Teams': ['teams:read', 'teams:write'],
  'Approvals': ['approvals:read', 'approvals:write'],
  'Analytics': ['analytics:read'],
  'Alerts': ['alerts:read', 'alerts:write'],
  'Memory': ['memory:read', 'memory:write'],
  'Settings': ['settings:read'],
};

export default function ApiKeysPage() {
  const [keys, setKeys] = useState<TenantApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    scopes: [] as string[],
    role: 'DEVELOPER' as string,
    expiresAt: '',
    rateLimitPerMinute: 60,
    sourceApp: 'api',
  });
  const [revokeId, setRevokeId] = useState<string | null>(null);

  const load = () => {
    tenantApiKeyApi.list().then(setKeys).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  async function handleCreate() {
    setSaving(true);
    try {
      const payload: Parameters<typeof tenantApiKeyApi.create>[0] = {
        name: form.name,
        scopes: form.scopes,
        role: form.role,
        rateLimitPerMinute: form.rateLimitPerMinute,
        sourceApp: form.sourceApp || undefined,
      };
      if (form.description) payload.description = form.description;
      if (form.expiresAt) payload.expiresAt = new Date(form.expiresAt).toISOString();
      const result = await tenantApiKeyApi.create(payload);
      setNewKey(result.key || result.rawKey);
      setShowModal(false);
      setForm({ name: '', description: '', scopes: [], role: 'DEVELOPER', expiresAt: '', rateLimitPerMinute: 60, sourceApp: 'api' });
      load();
    } finally {
      setSaving(false);
    }
  }

  async function handleRevoke(id: string) {
    await tenantApiKeyApi.revoke(id);
    setRevokeId(null);
    load();
  }

  function handleCopy() {
    if (newKey) {
      void navigator.clipboard.writeText(newKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function toggleScope(scope: string) {
    setForm(f => ({
      ...f,
      scopes: f.scopes.includes(scope) ? f.scopes.filter(s => s !== scope) : [...f.scopes, scope],
    }));
  }

  function selectAllScopes() {
    const allScopes = Object.values(SCOPE_CATEGORIES).flat();
    setForm(f => ({ ...f, scopes: allScopes }));
  }

  function clearAllScopes() {
    setForm(f => ({ ...f, scopes: [] }));
  }

  return (
    <div className={styles.page}>
      <PageGuide pageKey="api-keys" />

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}><Shield size={22} /> API Keys</h1>
          <p className={styles.subtitle}>Server-to-server API keys with granular scope control. Use with <code>x-enska-api-key</code> header.</p>
        </div>
        <button id="create-api-key" className={styles.createBtn} onClick={() => setShowModal(true)}>
          <Plus size={16} /> New API Key
        </button>
      </div>

      {/* One-time key reveal */}
      {newKey && (
        <div className={styles.keyReveal}>
          <div className={styles.keyRevealHeader}>
            <Check size={18} className={styles.successIcon} />
            <strong>API Key created — copy it now. It won't be shown again.</strong>
            <button className={styles.dismissBtn} onClick={() => setNewKey(null)}><X size={14} /></button>
          </div>
          <div className={styles.keyDisplay}>
            <code className={styles.keyCode}>{newKey}</code>
            <button id="copy-api-key" className={styles.copyBtn} onClick={handleCopy}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div className={styles.table}>
        <div className={styles.thead}>
          <div className={styles.theadCell}>Name</div>
          <div className={styles.theadCell}>Prefix</div>
          <div className={styles.theadCell}>Scopes</div>
          <div className={styles.theadCell}>Role</div>
          <div className={styles.theadCell}>Rate</div>
          <div className={styles.theadCell}>Usage</div>
          <div className={styles.theadCell}>Status</div>
          <div className={styles.theadCell}></div>
        </div>

        {loading && [1, 2].map(i => <div key={i} className={`${styles.row} ${styles.skeleton}`} />)}

        {!loading && keys.length === 0 && (
          <SettingsEmptyState
            icons={[
              { icon: <Key size={18} />, color: '#6366f1' },
              { icon: <Shield size={18} />, color: '#10b981' },
            ]}
            title="No API keys yet"
            description="Create an API key to start integrating with the platform. Each key has granular scope control and rate limiting."
            buttonLabel="Create API Key"
            onAction={() => setShowModal(true)}
          />
        )}

        {!loading && keys.map(k => (
          <div key={k.id} className={styles.row}>
            <div className={styles.cell}>
              <div>
                <strong>{k.name}</strong>
                {k.description && <div className={styles.cellSub}>{k.description}</div>}
              </div>
            </div>
            <div className={styles.cell}><code className={styles.prefix}>{k.keyPrefix}…</code></div>
            <div className={styles.cell}>
              <span className={styles.scopeCount} title={k.scopes?.join(', ') || 'none'}>
                {k.scopes?.length || 0} scopes
              </span>
            </div>
            <div className={styles.cell}><span className={styles.roleBadge}>{k.role || 'DEVELOPER'}</span></div>
            <div className={styles.cell}>{k.rateLimitPerMinute}/min</div>
            <div className={styles.cell}>{(k.usageCount || 0).toLocaleString()}</div>
            <div className={styles.cell}>
              <span className={`${styles.statusBadge} ${k.active ? styles.active : styles.revoked}`}>
                {k.active ? 'Active' : 'Revoked'}
              </span>
            </div>
            <div className={styles.cell}>
              {k.active && (
                <button className={styles.revokeBtn} onClick={() => setRevokeId(k.id)} title="Revoke key">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showModal && (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className={`${styles.modal} ${styles.modalWide}`}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Create API Key</h2>
              <button className={styles.dismissBtn} onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>Key Name <span className={styles.req}>*</span></label>
                  <input id="key-name" className={styles.input} placeholder="e.g. Production Backend" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Role</label>
                  <select className={styles.input} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="ADMIN">Admin</option>
                    <option value="DEVELOPER">Developer</option>
                    <option value="OPERATOR">Operator</option>
                    <option value="VIEWER">Viewer</option>
                  </select>
                </div>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Description</label>
                <input className={styles.input} placeholder="What is this key used for?" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>Rate Limit (req/min)</label>
                  <input type="number" className={styles.input} value={form.rateLimitPerMinute} onChange={e => setForm(f => ({ ...f, rateLimitPerMinute: Number(e.target.value) }))} min={1} max={6000} />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Expiry Date</label>
                  <input type="date" className={styles.input} value={form.expiresAt} onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
                </div>
              </div>

              {/* Scopes */}
              <div className={styles.field}>
                <div className={styles.scopeHeader}>
                  <label className={styles.label}>Scopes <span className={styles.req}>*</span></label>
                  <div className={styles.scopeActions}>
                    <button type="button" className={styles.scopeActionBtn} onClick={selectAllScopes}>Select all</button>
                    <button type="button" className={styles.scopeActionBtn} onClick={clearAllScopes}>Clear</button>
                  </div>
                </div>
                <div className={styles.scopeGrid}>
                  {Object.entries(SCOPE_CATEGORIES).map(([category, categoryScopes]) => (
                    <div key={category} className={styles.scopeCategory}>
                      <div className={styles.scopeCategoryName}>{category}</div>
                      <div className={styles.scopeItems}>
                        {categoryScopes.map(scope => (
                          <label key={scope} className={styles.scopeItem}>
                            <input
                              type="checkbox"
                              checked={form.scopes.includes(scope)}
                              onChange={() => toggleScope(scope)}
                            />
                            <span className={styles.scopeLabel}>{scope.split(':')[1]}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <span className={styles.scopeSelected}>{form.scopes.length} scope{form.scopes.length !== 1 ? 's' : ''} selected</span>
              <button className={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button id="confirm-create-key" className={styles.confirmBtn} onClick={handleCreate} disabled={!form.name.trim() || form.scopes.length === 0 || saving}>
                {saving ? 'Creating…' : 'Create Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!revokeId}
        onCancel={() => setRevokeId(null)}
        onConfirm={() => revokeId && handleRevoke(revokeId)}
        title="Revoke API Key"
        description="This will permanently revoke this API key. Any integrations using it will immediately stop working."
        confirmLabel="Revoke Key"
        variant="danger"
      />
    </div>
  );
}
